import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Control, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useDateConstraints } from "./useDateConstraints";
import type { CampaignBudgetMinimums } from "../api/useCampaignBudgetMinimums";
import { useCampaignBudgetMinimums } from "../api/useCampaignBudgetMinimums";
import type {
  CampaignJobBoardFormInput,
  CampaignJobBoardFormValues,
} from "../components/CampaignJobBoardForm/types";
import { createCampaignJobBoardSchema } from "../validationSchema";

import type { AddCampaignJobBoardRequest } from "@/types/request/AddCampaignJobBoardRequest";
import type { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";

export interface UseCampaignJobBoardFormParams {
  campaignId: string;
  campaignJobBoard?: CampaignJobBoardType;
  clientId?: string;
  isEdit?: boolean;
  onSuccess?: () => void;
}

export interface UseCampaignJobBoardFormReturn {
  form: UseFormReturn<
    CampaignJobBoardFormInput,
    unknown,
    CampaignJobBoardFormValues
  >;
  control: Control<
    CampaignJobBoardFormInput,
    unknown,
    CampaignJobBoardFormValues
  >;
  formValues: CampaignJobBoardFormInput;
  dateConstraints: {
    minStartDate: Date;
    maxStartDate: Date;
    minEndDate: Date;
  };
  useDifferentCurrentMonthBudget: boolean;
  showCurrentMonthBudget: boolean;
  daysRemainingInMonth: number | null;
  isIndeed: boolean;
  setIntegrationKey: (key: string | undefined) => void;
  handleCancel: () => void;
  handleSubmit: (
    onSubmitForm: (data: AddCampaignJobBoardRequest) => void
  ) => (data: CampaignJobBoardFormValues) => void;
  budgetMinimums: CampaignBudgetMinimums | undefined;
}

export function useCampaignJobBoardForm({
  campaignId,
  campaignJobBoard,
  clientId,
  isEdit = false,
  onSuccess,
}: UseCampaignJobBoardFormParams): UseCampaignJobBoardFormReturn {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const initialModuleId = campaignJobBoard?.clientIntegrationModuleId ?? "";

  const schemaRef = useRef(createCampaignJobBoardSchema(t, isEdit));

  const form = useForm<
    CampaignJobBoardFormInput,
    unknown,
    CampaignJobBoardFormValues
  >({
    resolver: (...args) => zodResolver(schemaRef.current)(...args),
    mode: "onChange",
    defaultValues: {
      currentMonthBudget: campaignJobBoard?.currentMonthBudget ?? undefined,
      endDate: campaignJobBoard?.endDate ?? undefined,
      clientIntegrationModuleId: initialModuleId,
      monthlyBudget: campaignJobBoard?.monthlyBudget ?? undefined,
      startDate: campaignJobBoard?.startDate ?? "",
      useDifferentCurrentMonthBudget:
        !isEdit &&
        campaignJobBoard?.currentMonthBudget !== undefined &&
        campaignJobBoard?.currentMonthBudget !== null,
      useProRataFirstMonth: false,
    },
  });

  const { control, watch, setValue } = form;
  const formValues = watch();
  const currentMonthBudget = watch("currentMonthBudget");
  const useDifferentCurrentMonthBudget = watch("useDifferentCurrentMonthBudget") ?? false;
  const selectedModuleId = watch("clientIntegrationModuleId") || initialModuleId;

  // Stable integration key (e.g. "indeed") emitted by IntegrationModuleSelect — distinct from the
  // translated display label. Pro rata first month is Indeed-only.
  const [integrationKey, setIntegrationKey] = useState<string | undefined>(
    campaignJobBoard?.integrationName ?? undefined
  );

  useEffect(() => {
    if (campaignJobBoard?.integrationName) {
      setIntegrationKey((prev) => prev ?? campaignJobBoard.integrationName ?? undefined);
    }
  }, [campaignJobBoard?.integrationName]);

  const isIndeed = (integrationKey ?? "").toLowerCase() === "indeed";

  const { data: budgetMinimums } = useCampaignBudgetMinimums({
    campaignId,
    clientIntegrationModuleId: selectedModuleId || undefined,
  });

  const preventEndDateClear = isEdit && isIndeed && !!campaignJobBoard?.endDate;

  useMemo(() => {
    schemaRef.current = createCampaignJobBoardSchema(t, isEdit, budgetMinimums, {
      preventEndDateClear,
      isIndeed,
    });
  }, [t, isEdit, budgetMinimums, preventEndDateClear, isIndeed]);

  useEffect(() => {
    if (!isEdit && currentMonthBudget !== undefined && currentMonthBudget !== null) {
      setValue("useDifferentCurrentMonthBudget", true);
    }
  }, [currentMonthBudget, isEdit, setValue]);

  useEffect(() => {
    if (!useDifferentCurrentMonthBudget) {
      setValue("currentMonthBudget", undefined);
    }
  }, [useDifferentCurrentMonthBudget, setValue]);

  // Pro rata first month and the manual current-month budget are mutually exclusive, and pro rata
  // is Indeed-only. When Indeed is selected, drop the manual control; otherwise clear the flag.
  useEffect(() => {
    if (isIndeed) {
      setValue("useDifferentCurrentMonthBudget", false);
      setValue("currentMonthBudget", undefined);
    } else {
      setValue("useProRataFirstMonth", false);
    }
  }, [isIndeed, setValue]);

  const dateConstraintsResult = useDateConstraints({
    formValues,
    isEdit,
    isIndeed,
  });

  const handleCancel = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    } else {
      const path = clientId
        ? `/clients/${clientId}/campaigns/${campaignId}`
        : `/campaigns/${campaignId}`;
      navigate(path);
    }
  }, [campaignId, clientId, navigate, onSuccess]);

  const handleSubmit = useCallback(
    (onSubmitForm: (data: AddCampaignJobBoardRequest) => void) => {
      return (data: CampaignJobBoardFormValues) => {
        onSubmitForm({ ...data, campaignId });
      };
    },
    [campaignId]
  );

  return {
    form,
    control,
    formValues,
    dateConstraints: dateConstraintsResult.dateConstraints,
    useDifferentCurrentMonthBudget,
    showCurrentMonthBudget: dateConstraintsResult.showCurrentMonthBudget,
    daysRemainingInMonth: dateConstraintsResult.daysRemainingInMonth,
    isIndeed,
    setIntegrationKey,
    handleCancel,
    handleSubmit,
    budgetMinimums,
  };
}
